import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../common/ToastProvider';

// Landing page for the Approve/Deny links embedded in extension-request
// emails. Reachable both logged-out (via PublicRoutes) and logged-in (via
// the authenticated route tree) — same component either way, mirroring
// useAttendanceCheckIn.js's login-redirect-then-resume pattern. The link
// itself is a plain deep link (?id=&action=); the real permission check
// still happens server-side in the existing approve/deny routes.
export default function useExtensionActionLanding() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user, loading: authLoading } = useAuth();

  const id = searchParams.get('id');
  const action = searchParams.get('action') === 'deny' ? 'deny' : 'approve';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [request, setRequest] = useState(null);
  const [hours, setHours] = useState(48);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // 'approved' | 'denied' once done

  // Redirect to login if not authenticated, preserving this exact URL so
  // useSimpleLogin.js brings the user straight back here once signed in.
  useEffect(() => {
    if (!authLoading && !user) {
      const currentPath = `/extensions/action?${searchParams.toString()}`;
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
    }
  }, [user, authLoading, navigate, searchParams]);

  const fetchRequest = useCallback(async () => {
    if (!id) {
      setError('This link is missing a request id.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const response = await apiService.listExtensions({ id });
      const row = response?.requests?.[0];
      if (!row) {
        setError("You don't have permission to review this request, or it no longer exists.");
      } else {
        setRequest(row);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load extension request.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (user && id) fetchRequest();
  }, [user, id, fetchRequest]);

  const handleApprove = async () => {
    const n = Number(hours);
    if (!Number.isFinite(n) || n <= 0) {
      setError('Enter a positive number of hours.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await apiService.approveExtension(id, { durationHours: n, note: note.trim() || undefined });
      setResult('approved');
      toast.success('Extension request approved');
    } catch (err) {
      setError(err?.errorData?.message || err?.message || 'Failed to approve request.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeny = async () => {
    setSubmitting(true);
    setError('');
    try {
      await apiService.denyExtension(id, { note: note.trim() || undefined });
      setResult('denied');
      toast.success('Extension request denied');
    } catch (err) {
      setError(err?.errorData?.message || err?.message || 'Failed to deny request.');
    } finally {
      setSubmitting(false);
    }
  };

  return {
    authLoading,
    user,
    action,
    loading,
    error,
    request,
    result,
    hours,
    setHours,
    note,
    setNote,
    submitting,
    handleApprove,
    handleDeny,
  };
}
